import type {ReactElement} from 'react';
import type {TemplateThumbnail, TemplatesForInspirationTileProps} from './templates-for-inspiration-tile.types';
import {MIN_TEMPLATES_TO_RENDER, TEMPLATES_ILLUSTRATION_ASSET} from './templates-for-inspiration-tile.types';
import {EntityThumbnailTile} from '@Components/ai-chat-window/components/entity-thumbnail-tile/entity-thumbnail-tile';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {openAttachTemplateGalleryDialog} from '@Libraries/ai-chat.library';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {getAssetUrl} from '@Utils/s3.util';
import {AiChatTemplateAttachOrigin, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {IconType} from '@Components/icon-v2/icon.types';

export function TemplatesForInspirationTile({chatId, variant}: TemplatesForInspirationTileProps): ReactElement {
  const {state} = useAiChat();
  const {actions: promptBoxActions} = useAiPromptBox(state.promptBoxId);
  const templateThumbnails: TemplateThumbnail[] = state.getRecommendedTemplates(chatId).map((template) => ({
    id: template.hashedId,
    previewUrl: template.previewUrl,
    altText: '',
    pageHashedId: template.pageHashedId,
  }));

  const handleViewMore = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TEMPLATES_VIEWMORE);
    openAttachTemplateGalleryDialog(AiChatTemplateAttachOrigin.VIEW_MORE, state.getTemplateSearchTerm(chatId) ?? undefined);
  };

  const handleTemplateClick = (template: TemplateThumbnail): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TEMPLATES_ATTACH, {[GA4EventParamName.TYPE]: AiChatTemplateAttachOrigin.RECOMMENDED_TEMPLATES});
    promptBoxActions.addDesignTemplateAttachments([
      {
        type: PromptAttachmentType.DESIGN_TEMPLATE,
        designId: template.id,
        previewURL: template.previewUrl,
        pageHashedId: template.pageHashedId,
      },
    ]);
  };

  return (
    <EntityThumbnailTile
      title={window.i18next.t('pmwjs_want_to_try_another_look')}
      subtitle={window.i18next.t('pmwjs_select_a_template_to_redesign_it')}
      subtitleIcon={'icon-mouse-click'}
      mobileSubtitle={window.i18next.t('pmwjs_redesign_a_template_with_ai')}
      illustrationUrl={getAssetUrl(TEMPLATES_ILLUSTRATION_ASSET)}
      thumbnails={templateThumbnails}
      minThumbnailsToRender={MIN_TEMPLATES_TO_RENDER}
      hoverAction={{icon: 'icon-paperclip', iconType: IconType.PREMIUM, label: window.i18next.t('pmwjs_attach')}}
      viewMoreAction={{label: window.i18next.t('pmwjs_view_more'), icon: 'icon-designs', onClick: handleViewMore}}
      onThumbnailClick={handleTemplateClick}
      variant={variant}
    />
  );
}
